import React, { useEffect, useState, useCallback } from "react";

/**
 * Locale-proof 24-hour time input.
 * Renders as a plain text field with an HH:mm mask so that Chrome / Edge /
 * Safari never inject AM-PM chrome regardless of the OS locale.
 *
 * Props:
 *  - value:     controlled "HH:mm" string (may be empty)
 *  - onChange:  called with the new "HH:mm" string (only when it's valid or empty)
 *  - disabled
 *  - className
 *  - data-testid
 *  - placeholder (defaults to "HH:mm")
 */
export function Time24Input({
  value,
  onChange,
  disabled = false,
  className = "",
  placeholder = "HH:mm",
  ...rest
}) {
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const normalize = useCallback((raw) => {
    // Keep only digits and optional colon
    const digits = String(raw || "").replace(/[^\d]/g, "").slice(0, 4);
    if (digits.length === 0) return "";
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }, []);

  const isValid = (s) => /^([01]\d|2[0-3]):[0-5]\d$/.test(s);

  const handleChange = (e) => {
    const next = normalize(e.target.value);
    setDraft(next);
    if (next === "" || isValid(next)) {
      onChange && onChange(next);
    }
  };

  const handleBlur = () => {
    if (draft === "") {
      onChange && onChange("");
      return;
    }
    // Try to auto-complete common short inputs like "9" -> "09:00", "930" -> "09:30"
    const digits = draft.replace(/[^\d]/g, "");
    let hh = "";
    let mm = "";
    if (digits.length === 1) {
      hh = `0${digits}`;
      mm = "00";
    } else if (digits.length === 2) {
      hh = digits;
      mm = "00";
    } else if (digits.length === 3) {
      hh = `0${digits[0]}`;
      mm = digits.slice(1);
    } else {
      hh = digits.slice(0, 2);
      mm = digits.slice(2, 4);
    }
    const h = Math.min(23, parseInt(hh, 10) || 0);
    const m = Math.min(59, parseInt(mm, 10) || 0);
    const final = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    setDraft(final);
    onChange && onChange(final);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      spellCheck={false}
      pattern="^([01]\d|2[0-3]):[0-5]\d$"
      maxLength={5}
      placeholder={placeholder}
      disabled={disabled}
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      {...rest}
    />
  );
}

export default Time24Input;
